import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import CustomLink from '../CustomLink';
import { Logo } from '../Logo';
import { useUser } from '../../shared/use-user';
import { useRouting } from '../../shared/use-routing';
import { pagePathnames } from '../../pages/page-routes';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { SUPPORT_EMAIL } from '../../shared/links';
import { uiConfig, getLegalLinks, getLegalEntity } from '../../app-ui-config';
import styles from './AppFooter.css';

const cx = classnames.bind(styles);

function AppFooter({ className }) {
    const { userData } = useUser();
    const { params, buildPath, lang } = useRouting();
    const { t } = useTranslation();
    const classes = cx({
        root: true,
        [className]: Boolean(className)
    });
    const legalLinks = getLegalLinks(userData.legalRegion, lang);
    const legalEntity = getLegalEntity(userData.legalRegion);

    if (Boolean(params.subscriptionId) || Boolean(params.orderId)) {
        return <div />;
    }

    return (
        <footer className={classes}>
            <div className={styles.wrapper}>
                <nav className={styles.bottomDivider} aria-label={t('common.legalLinks')}>
                    <ul className={styles.links}>
                        <li>
                            <CustomLink
                                to={legalLinks.termsOfUse}
                                target='_blank'
                                className={styles.link}
                            >
                                {t('common.termsOfUse')}
                            </CustomLink>
                        </li>
                        <li>
                            <CustomLink
                                to={legalLinks.privacyPolicy}
                                target='_blank'
                                className={styles.link}
                            >
                                {t('common.privacyPolicy')}
                            </CustomLink>
                        </li>
                        <li>
                            <CustomLink
                                to={legalLinks.cookiePolicy}
                                target='_blank'
                                className={styles.link}
                            >
                                {t('common.cookiePolicy')}
                            </CustomLink>
                        </li>
                        <li>
                            <CustomLink
                                to={buildPath(pagePathnames.contactUs)}
                                className={styles.link}
                            >
                                {t('common.contactUsLink')}
                            </CustomLink>
                        </li>
                        <li>
                            <CustomLink
                                to={uiConfig.faqForEuConsumersLinks[lang] || uiConfig.faqForEuConsumersLinks.en}
                                target='_blank'
                                className={styles.link}
                            >
                                {t('common.faqForEuConsumers')}
                            </CustomLink>
                        </li>
                        <li>
                            <CustomLink
                                to={buildPath(pagePathnames.blog)}
                                target='_blank'
                                className={styles.link}
                            >
                                {t('common.blog')}
                            </CustomLink>
                        </li>
                    </ul>
                </nav>
                <div className={styles.bottomWrapper}>
                    <Logo inApp url={buildPath(pagePathnames.dashboard)} />
                    <div>
                        <address className={styles.address} style={{ marginBottom: 16 }}>
                            © {legalEntity.year} — {new Date().getFullYear()} {legalEntity.company} <br /> {legalEntity.address}
                        </address>
                        <ul className={styles.links}>
                            {Boolean(legalEntity.telephone) && (
                                <li>
                                    <CustomLink
                                        to={legalEntity.telephone.link}
                                        target='_blank'
                                        className={styles.link}
                                    >
                                        {legalEntity.telephone.text}
                                    </CustomLink>
                                </li>
                            )}
                            <li>
                                <CustomLink
                                    to={`mailto:${SUPPORT_EMAIL}`}
                                    target='_blank'
                                    className={styles.link}
                                    onClick={() => logEvent(EVENT_TYPES.SUPPORT_EMAIL_CLICKED)}
                                >
                                    {SUPPORT_EMAIL}
                                </CustomLink>
                            </li>
                        </ul>
                    </div>
                    <div className={styles.separator} />
                </div>
            </div>
        </footer>
    );
}

AppFooter.propTypes = {
    className: PropTypes.string
};

AppFooter.defaultProps = {
    className: ''
};

export {
    AppFooter
};
